import { ImgHTMLAttributes, useEffect, useState } from "react";
import { acquireToken, authEnabled } from "../auth/authConfig";

export interface AuthenticatedImageProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  /** Entra ID scope used to acquire the bearer token for fetching the image. */
  scope?: string;
}

/**
 * Renders an image whose URL sits behind Entra ID auth (e.g. Easy Auth).
 * When auth is enabled and a scope is provided, the image is fetched with a
 * bearer token and rendered from a blob object URL. When auth is disabled
 * (or fetching fails), it falls back to a plain <img src>.
 */
export default function AuthenticatedImage({
  src,
  alt,
  scope,
  ...imgProps
}: AuthenticatedImageProps) {
  const useAuthFetch = authEnabled && Boolean(scope);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!useAuthFetch || !src) {
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setFailed(false);

    const fetchImage = async () => {
      try {
        const token = await acquireToken(scope!);
        const headers: HeadersInit = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const response = await fetch(src, { headers });
        if (!response.ok) {
          throw new Error(`Image request failed with ${response.status}`);
        }
        const blob = await response.blob();
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src, scope, useAuthFetch]);

  // Auth disabled (or fetch failed): behave exactly like a plain <img>.
  if (!useAuthFetch || failed) {
    return <img src={src} alt={alt} {...imgProps} />;
  }

  // Still loading the authenticated image.
  if (!objectUrl) {
    return null;
  }

  return <img src={objectUrl} alt={alt} {...imgProps} />;
}
